import React, { useCallback, useEffect, useState } from 'react';
import googleApi from '../services/googleApi';
import AccordionSection from './AccordionSection';
import Icon from './Icon';
import { managerInitials } from './RelationshipManagersField';
import './RelationshipManagers.css';
import './MeetingsSection.css';
import './EmailsSection.css';

const dayOf = (iso) => (iso && iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso));

const formatDay = (iso) => (iso ? dayOf(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const formatSince = (iso) => (iso ? dayOf(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : '');

const Thread = ({ thread, onOpenPerson }) => (
  <li className="meet email">
    <div className="meet__line">
      <span className="meet__when">{formatDay(thread.latest_at)}</span>
      <span className="meet__title" title={thread.subject}>{thread.subject}</span>
      <span className="email__count" title={`${thread.message_count} message${thread.message_count === 1 ? '' : 's'}`}>
        {thread.message_count}
      </span>
      {thread.tileward.length > 0 && (
        <span className="meet__team">
          {thread.tileward.map(item => (
            <span key={item.email || item.name} className="rm-avatar meet__avatar" title={item.name}>{managerInitials(item.name)}</span>
          ))}
        </span>
      )}
    </div>
    {(thread.people.length > 0 || thread.others.length > 0) && (
      <ul className="meet__people">
        {thread.people.map(person => (
          <li key={person.contact_id}>
            {onOpenPerson
              ? <button type="button" className="rm-link" onClick={() => onOpenPerson(person.contact_id)}>{person.name}</button>
              : <strong>{person.name}</strong>}
            {person.title && <span className="meet__meta"> – {person.title}</span>}
          </li>
        ))}
        {thread.others.map(other => (
          <li key={other.email}>
            {other.name || other.email}<span className="meet__meta"> · not on file</span>
          </li>
        ))}
      </ul>
    )}
    {thread.gmail_url && (
      <div className="meet__actions">
        <a href={thread.gmail_url} target="_blank" rel="noopener noreferrer" className="meet__notes">
          <Icon name="external-link" /> Open in Gmail
        </a>
      </div>
    )}
  </li>
);

/**
 * A company's email threads from the connected sales mailboxes (headers only): a summary line, five
 * threads at a time, newest first. Read-only. "Open in Gmail" appears only for a thread in your own mailbox.
 */
const EmailsSection = ({ companyId, companyName, onOpenPerson, dataVersion = 0 }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => googleApi.emails(companyId)
    .then(result => {
      setData(result);
      setError(null);
    })
    .catch(err => setError(err.message)), [companyId]);

  useEffect(() => {
    setData(null);
    setError(null);
  }, [companyId]);

  useEffect(() => {
    if (companyId) load();
  }, [companyId, dataVersion, load]);

  const showMore = async () => {
    setLoadingMore(true);
    try {
      const more = await googleApi.emails(companyId, data.next_before);
      setData(current => ({ ...current, threads: [...current.threads, ...more.threads], has_more: more.has_more, next_before: more.next_before }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const summary = data?.summary;
  const last = summary?.last_emailed;
  return (
    <AccordionSection
      id="email-section"
      className="email-section"
      open={open}
      onToggle={() => setOpen(current => !current)}
      title="Emails"
    >
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!data && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}

      {data && data.threads.length === 0 && (
        <p className="rm-field__empty">No emails with {companyName || 'this company'} since {formatSince(summary.since)}.</p>
      )}

      {data && data.threads.length > 0 && (
        <>
          <p className="meet-summary">
            {last && `Last emailed ${formatDay(last.sent_at)}${last.by ? ` by ${last.by}` : ''}`}
            {` · ${summary.threads} thread${summary.threads === 1 ? '' : 's'} since ${formatSince(summary.since)}`}
          </p>
          <ul className="meet-list">
            {data.threads.map(thread => (
              <Thread key={`${thread.latest_at}-${thread.subject}`} thread={thread} onOpenPerson={onOpenPerson} />
            ))}
          </ul>
          {data.has_more && (
            <button type="button" className="rm-link" onClick={showMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Show more'}
            </button>
          )}
        </>
      )}
    </AccordionSection>
  );
};

export default EmailsSection;
